import { faSearch, faTimesCircle } from '@fortawesome/pro-light-svg-icons'
import { CustomerTypesInversedKeys } from '@teinor/erp/types/company/customer/customerTypes'
import { useState, useMemo, useCallback, useEffect, Fragment, memo } from 'react'
import { Link } from 'react-router-dom'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import locations from '../../../../../utils/globals/locations'
import { stringMatchWithSearch } from '../../../../../utils/globals/search'
import { ChangeValueFunc } from '../../../../../utils/globals/types'
import useCustomerTypesDict from '../../../../../utils/hooks/customer/useCustomerTypesDict'
import useIsLoading from '../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { getConfigParam, getAllSpecificPriceRules, getUserPermissions, getModulesBuyed } from '../../../../../utils/reducers/getters'
import { Routes } from '../../../../CreateRoutering/types'
import LoadingSpinner from '../../../../LoaderDecider/LoadingSpinner'
import { SearchBarWithDebounce } from '../../../../SearchBar'
import TwinIcon from '../../../../TwinIcon'
import { getFilteredRoutes } from '../functions'
import { VariablePrefixKeys, InversedVariablePrefixType, InversedVariablePrefixKeys } from '../types'
import { CustomChangeFunction } from '../../../../../forms/Input/types'
import { permissionCheck } from '../../../../PermissionChecker/function'
import { haveModuleBuyed } from '../../../../../utils/globals'
import { memoChecker } from '../../../../../utils/globals/components'

interface SearchMenuBaseProps {
    onClose: () => void
    routes: Routes
}

interface SearchMenuWithSearchBarProps extends SearchMenuBaseProps {}

export const SearchMenuWithSearchBar: React.FC<SearchMenuWithSearchBarProps> = ({...rest}) => {
    const { handleOnChangeSearchBar, handleOnKeyDown, searchValue, onChangeSearchBar } = useSearchMenuWithSearchBarLogic()
    return (
        <SearchMenu {...rest} handleOnChangeSearchBar={handleOnChangeSearchBar} searchValue={searchValue}>
            <SearchBarMenu value={searchValue.value} onChange={onChangeSearchBar} handleOnKeyDown={handleOnKeyDown} />
        </SearchMenu>
    )
}

export const useSearchMenuWithSearchBarLogic = () => {
    const [searchValue, setSearchValue] = useState<MySearchValueType>({
        value: '',
        type: 'general',
        parsedValue: '',
        isVariableTypingOn: false
    })
    const types: InversedVariablePrefixType = useMemo(() => {
        return {
            '@cliente:': 'customers',
            '@producto:': 'products'
        }
    }, [])
    const onChangeSearchBar: ChangeValueFunc = useCallback((value) => {
        let startWithVariable = value.startsWith('@')
        if (startWithVariable) {
            for (const key in types) {
                const valueSplited = value.toLowerCase().split(key)
                if (valueSplited.length > 1) {
                    return setSearchValue({ type: types[key as InversedVariablePrefixKeys], value, parsedValue: valueSplited[1].trim(), isVariableTypingOn: false })
                }
            }
        }
        setSearchValue({ value, type: 'general', parsedValue: value, isVariableTypingOn: startWithVariable })
    }, [types])

    const focusSearchbar = useCallback(() => {
        const input = document.querySelector<HTMLInputElement>('.second_left_menu input')
        input?.focus()
    }, [])

    const handleOnChangeSearchBar = useCallback((value: string) => {
        const parsedValue = value + ':'
        onChangeSearchBar(parsedValue)
        focusSearchbar()
    }, [onChangeSearchBar, focusSearchbar])

    const handleOnKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            // @ts-ignore
            document.getElementsByClassName('search_modal_item')?.[0]?.click()
        }
    }, [])

    return { searchValue, handleOnChangeSearchBar, handleOnKeyDown, onChangeSearchBar  }
}

interface SearchBarMenuProps {
    onChange: ChangeValueFunc
    value: string
    handleOnKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

const SearchBarMenu: React.FC<SearchBarMenuProps> = memo(({ onChange, handleOnKeyDown, value }) => {
    const { t } = useTwinTranslation()
    return (
        <SearchBarWithDebounce placeholder={t('searchDots', 'Buscar...')} searchValue={value} onChange={onChange} className='text-gray-51 main_menu_search' onKeyDown={handleOnKeyDown} />
    )
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['onChange', 'handleOnKeyDown', 'value']))

interface SearchMenuProps extends SearchMenuBaseProps {
    searchValue: MySearchValueType
    children?: React.ReactNode
    handleOnChangeSearchBar: CustomChangeFunction
}
const SearchMenu: React.FC<SearchMenuProps> = ({ routes, onClose, children, handleOnChangeSearchBar, searchValue }) => {
    const { types } = useSearchMenuLogic()
    const componentsDict: Record<VariablePrefixKeys, React.FC<SearchModalRoutesBaseProps>> = {
        general: SearchModalRoutes,
        customers: SearchModalCustomerRoutes,
        products: SearchModalProductRoutes,
    }
    const Component = componentsDict[searchValue.type]
    let renderThis: JSX.Element | null = null

    if (searchValue.isVariableTypingOn) {
        renderThis = <SearchModalVariables searchValue={searchValue} handleOnChangeSearchbar={handleOnChangeSearchBar} types={types as InversedVariablePrefixType} />
    } else {
        renderThis = <Component {...{ searchValue, onClose, routes }} />
    }

    return (
        <div className='flex flex-col bg-white z-5 absolute mt-38 p-15 rounded-8 shadow-lg mr-15 w-350 search_menu_box'>
            {children}
            {renderThis}
        </div>
    )
}
interface MySearchValueType {
    value: string,
    type: VariablePrefixKeys,
    parsedValue: string,
    isVariableTypingOn: boolean
}

const useSearchMenuLogic = () => {
    const types: TwinDictionary = useMemo(() => {
        const modulesBuyed = getModulesBuyed()
        const result: TwinDictionary = {}
        const productPermissions = getUserPermissions('product.profile.main')
        if (permissionCheck(productPermissions, 'read') && haveModuleBuyed(['products'], modulesBuyed)){
            result['@producto'] = 'products'
        }
        const customerPermissions = getUserPermissions('customer.profile.main')
        if (permissionCheck(customerPermissions, 'read') && haveModuleBuyed(['tasks'], modulesBuyed)){
            result['@cliente'] = 'customers'
        }
        return result
    }, [])
    const focusSearchbar = useCallback(() => {
        const input = document.querySelector<HTMLInputElement>('.second_left_menu input')
        input?.focus()
    }, [])

    useEffect(() => {
        focusSearchbar()
    }, [focusSearchbar])

    return { types }
}

interface SearchModalVariablesProps {
    handleOnChangeSearchbar: (value: string) => void
    searchValue: MySearchValueType
    types: InversedVariablePrefixType
}

const SearchModalVariables: React.FC<SearchModalVariablesProps> = ({ searchValue, handleOnChangeSearchbar, types }) => {
    const renderThis: JSX.Element[] = []
    for (const key in types) {
        const variable = key.replace(':', '')
        const matchesSearch = stringMatchWithSearch(searchValue.parsedValue, key)
        if (matchesSearch) {
            renderThis.push(
                <div className='text-green-21 mt-5 cursor-pointer opacity-80 search_variables search_modal_item' onClick={() => handleOnChangeSearchbar(variable)} key={variable}>
                    {variable}
                </div>
            )
        }
    }

    if (!renderThis.length) {
        renderThis.push(
            <SearchModalFeedback searchValue={searchValue} key='searchModalFeedback' />
        )
    }

    return (
        <div className='flex flex-col mt-5 overflow-auto min-h-100 max-h-700'>
            {renderThis}
        </div>
    )
}

interface SearchModalRoutesBaseProps extends SearchModalRoutesBaseLogicProps {
    onClose: () => void
    routes: Routes
}

const SearchModalRoutes: React.FC<SearchModalRoutesBaseProps> = ({ searchValue, routes, onClose }) => {
    const filteredItems = getFilteredRoutes(routes, searchValue.parsedValue)
    const renderThis: JSX.Element[] = []
    for (const key in filteredItems) {
        const element = filteredItems[key]
        const children: JSX.Element[] = []
        for (const value in element.displayItems) {
            const child = element.displayItems[value]
            if (child.finalTo) {
                children.push(
                    <SingleRouteItem finalTo={child.finalTo} id={value} onClose={onClose} leftSideText={child.parentText} rightSideText={child?.displayText} key={value} />
                )
            }
        }
        const isLast = key === String(filteredItems.length - 1)
        renderThis.push(
            <div key={key} className={`mt-8 p-5 ${!isLast ? 'border-b border-gray-EE' : ''}`}>
                <h3 className='text-gray-51'>{element.title}</h3>
                <div className='flex flex-col mt-3 regular14' >{children}</div>
            </div>
        )
    }
    if (!renderThis.length) {
        renderThis.push(
            <SearchModalFeedback searchValue={searchValue} key='searchModalFeedback' />
        )
    }

    return (
        <div className='mt-5 overflow-auto min-h-100 max-h-700'>
            {renderThis}
        </div>
    )
}

interface SingleRouteItemProps {
    id: string
    finalTo: string
    image?: string
    leftSideText?: string
    rightSideText: string
    onClose: () => void
}

const SingleRouteItem: React.FC<SingleRouteItemProps> = ({ onClose, id, finalTo, image, leftSideText, rightSideText }) => {
    return (
        <Link key={id} className='flex flex-row text-gray-51 cursor-pointer hover:bg-green-DB p-3 mt-3 gap-x-8 search_modal_item' to={finalTo} onClick={onClose}>
            <div className='flex flex-row gap-x-10'>
                {image ? <img src={image} className='h-30 w-30 object-cover' alt='' /> : null}
                {leftSideText ? <span className='max-w-130 twin_elipsis opacity-80'>{leftSideText}</span> : null}
            </div>
            <span className='flex flex-auto w-1 twin_elipsis'>{rightSideText}</span>
        </Link >
    )
}

const SearchModalCustomerRoutes: React.FC<SearchModalRoutesBaseProps> = ({ searchValue, onClose }) => {
    const renderThis: JSX.Element[] = []
    const { loading, filteredItems } = useSearchModalCustomerRoutesLogic({ searchValue })
    const { dictCustomerTypeComplete } = useCustomerTypesDict()
    if (filteredItems) {
        for (const item of filteredItems) {
            const customerCode = item?.code ? item?.code + ' - ' : ''
            if (item.finalTo) {
                renderThis.push(
                    <SingleRouteItem id={item?.id} finalTo={item?.finalTo} onClose={onClose} leftSideText={dictCustomerTypeComplete[item?.customer_type]?.name} rightSideText={customerCode + item?.name} />
                )
            }
        }
    }

    if (!renderThis.length || loading) {
        renderThis.push(
            <SearchModalFeedback searchValue={searchValue} loading={loading} />
        )
    }

    return (
        <div className='flex flex-col mt-5 overflow-auto min-h-100 max-h-700'>
            {renderThis}
        </div>
    )
}

interface SearchModalRoutesBaseLogicProps {
    searchValue: MySearchValueType
    routes?: Routes
}

const useSearchModalCustomerRoutesLogic = ({ searchValue }: SearchModalRoutesBaseLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const [filteredItems, setFilteredItems] = useState<TwinDictionary[] | null>(null)

    const getData = useCallback(async (value: string) => {
        startLoading()
        const data: any[] = []
        const showAllCustomersTogether = getConfigParam('showAllCustomersTogether') === 'on' ? true : false
        const dictTypeCustomerLink: Record<CustomerTypesInversedKeys, string> = {
            0: 'customers',
            1: 'customersB2B',
            2: 'contactsB2B',
            3: 'contacts',
        }
        const myValue = `%${value}%`
        let myWhere = {}
        if (value.length) {
            myWhere = { "$or": { "Customer.code": { "$iLike": myValue }, "Customer.name": { "$iLike": myValue }, "fiscal_name": { "$iLike": myValue }, "Customer.surname": { "$iLike": myValue }, "customer_type": { "$iLike": myValue }, "Customer.email": { "$iLike": myValue }, "Customer.phone": { "$iLike": myValue }, "Customer.mobile": { "$iLike": myValue }, "CustomerOrigin.name": { "$iLike": myValue }, "ResponsibleEmployeeId": { "$iLike": myValue } } }
        }
        const result = await twinFetchPostJSON('/api/app/customer/getAllCustomers', {
            limit: 30,
            offset: 0,
            order: [["id", "desc"]],
            "where": myWhere
        })
        if (result) {
            for (const item of result) {
                data.push({
                    id: item?.id,
                    name: item?.name,
                    code: item?.code,
                    customer_type: item?.customer_type,
                    finalTo: locations.customer + '/' + (showAllCustomersTogether ? 'allCustomers' : dictTypeCustomerLink[item?.customer_type as CustomerTypesInversedKeys]) + '/' + item?.id + '/dashboard'
                })
            }
            endLoading()
            return setFilteredItems(data)
        }
    }, [endLoading, startLoading, setFilteredItems])

    useEffect(() => {
        getData(searchValue.parsedValue)
    }, [searchValue.parsedValue, getData])

    return { loading, filteredItems }
}

const SearchModalProductRoutes: React.FC<SearchModalRoutesBaseProps> = ({ searchValue, onClose }) => {
    const { loading, filteredItems } = useSearchModalProductRoutesLogic({ searchValue })
    const renderThis: JSX.Element[] = []
    if (filteredItems) {
        for (const item of filteredItems) {
            if (item?.finalTo) {
                renderThis.push(
                    <SingleRouteItem id={item?.id} finalTo={item?.finalTo} onClose={onClose} image={item?.image} rightSideText={item?.name || item?.reference} leftSideText={item?.name ? item?.reference : ''} key={item.id} />
                )
            }
        }
    }

    if (!renderThis.length || loading) {
        renderThis.push(
            <SearchModalFeedback searchValue={searchValue} loading={loading} key='searchModalFeedback' />
        )
    }

    return (
        <div className='flex flex-col mt-5 overflow-auto min-h-100 max-h-700'>
            {renderThis}
        </div>
    )
}

const useSearchModalProductRoutesLogic = ({ searchValue }: SearchModalRoutesBaseLogicProps) => {
    const [filteredItems, setFilteredItems] = useState<TwinDictionary[] | null>(null)
    const { startLoading, endLoading, loading } = useIsLoading()
    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRules = getAllSpecificPriceRules()
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [])

    const getData = useCallback(async (value: string) => {
        startLoading()
        const data: any[] = []
        const myValue = `%${value}%`
        let myWhere: TwinDictionary = {
            "SpecificPriceRuleId": defaultSpecificPriceRuleId,
            "productType": { "$or": [0, 3, 4] }
        }
        if (value.length) {
            myWhere = { ...myWhere, "$or": { "reference": { "$iLike": myValue }, "ProductLangs.name": { "$iLike": myValue } } }
        }
        const result = await twinFetchPostJSON('/api/app/product/getAllProductsComplete', {
            limit: 30,
            offset: 0,
            order: [["id", "desc"]],
            "where": myWhere
        })
        if (result) {
            for (const item of result) {
                data.push({
                    id: item?.id,
                    reference: item?.reference,
                    name: item?.['ProductLangs.name'],
                    image: item?.['ProductImages.url'],
                    finalTo: locations?.product + '/' + item?.id + '/main'
                })
            }
            endLoading()
            return setFilteredItems(data)
        }
    }, [endLoading, startLoading, setFilteredItems, defaultSpecificPriceRuleId])

    useEffect(() => {
        getData(searchValue.parsedValue)
    }, [searchValue.parsedValue, getData])

    return { loading, filteredItems }
}

interface SearchModalFeedbackProps {
    searchValue: MySearchValueType
    loading?: boolean
}

const SearchModalFeedback: React.FC<SearchModalFeedbackProps> = ({ searchValue, loading }) => {
    const { t } = useTwinTranslation()
    const searchForResults = t('searchForResults', 'Buscar dentro de la aplicación')
    const noSearchResults = t('noSearchResults', 'No hay resultados para tu búsqueda')
    const noResultsFeedback = searchValue?.parsedValue
    return (
        <div className='flex flex-col items-center justify-center text-gray-9D min-h-100' key='no_results'>
            {loading ?
                <LoadingSpinner /> :
                <Fragment>
                    <TwinIcon icon={noResultsFeedback ? faTimesCircle : faSearch} fontSize={36} />
                    <span className='mt-10'>{noResultsFeedback ? noSearchResults : searchForResults}</span>
                </Fragment>}
        </div>
    )
}

export default SearchMenu